import { useMutation } from '@tanstack/react-query';
import { ConsignmentSettingsGroupDetails, ConsignmentSettingsGroupModels, PrintingDefaultsModels } from './types';
import {
  bulkDeletePrintingDefaults,
  bulkPutPrintingDefaults,
  getGeneralConfigProfiles,
  getPrintingDefaults,
  postGeneralConfigProfile,
  putGeneralConfigProfile
} from 'src/services/relationshipmanagement';
import { useCommonUIFlowMutations } from '../util/helpers/commonHelpers';
import {
  GeneralConfigProfileDto,
  GeneralConfigProfileModel,
  PrintingDefaultModelBulkItemModel
} from 'src/models/relationshipmanagement';
import {
  buildConsignmentSettingsGroupModel,
  generateConsignmentSettingsGroupModels,
  handleSameNameCheck
} from './helpers';
import { ReadOnlyRefObject } from 'src/@core/hooks/useStateWithRef';
import { AssignedCustomersState } from '../util/types';
import { UpdateGeneralConfigProfileModel } from './GeneralConfigList';
import { useVigoToast } from 'src/@core/hooks/useVigoToast';
import { useAppSelector } from 'src/store/baseHooks';
import { useConfirmDialog } from 'src/@core/hooks/useConfirmDialog';

export const useConsignmentSettingsGroupMutations = (
  id?: string,
  consignmentSettingsGroupDetails?: ConsignmentSettingsGroupDetails | undefined,
  assignedCustomersRef?: ReadOnlyRefObject<AssignedCustomersState[]>,
  addRecentHistory?: (type: 'Add' | 'Edit', recordName: string) => void
) => {
  // Selectors
  const uiFlowMode = useAppSelector(state => state.uiflow.uiFlowMode);

  // Hooks
  const { vigoToast } = useVigoToast({});
  const { set: confirm } = useConfirmDialog();

  const updateGeneralConfigProfileMutation = useMutation({
    mutationFn: async (data: UpdateGeneralConfigProfileModel) => {
      await putGeneralConfigProfile(data.id, data.model);
    }
  });

  const removeOldDefaultMutation = useMutation({
    mutationFn: async (data: UpdateGeneralConfigProfileModel[]) => {
      await putGeneralConfigProfile(data[0].id, data[0].model).then(async () => {
        await putGeneralConfigProfile(data[1].id, data[1].model);
      });
    }
  });

  const copyConsignmentSettingsGroupMutation = useMutation({
    mutationKey: ['consignmentSettingsGroupCopyMutation'],
    mutationFn: async (data: { code: string; description: string; currentGroup: GeneralConfigProfileDto }) => {
      const copyToastId = vigoToast.loading('Copying Consignment Settings Group');

      let res: any;

      try {
        const existingProfile = (await getGeneralConfigProfiles({ filter: `name eq ${data.code}` })).data;
        if (existingProfile.length > 0) {
          /**
           * Group already exists
           */
          vigoToast.error('Group already exists', { id: copyToastId });

          return;
        }

        const printingDefaults = (
          await getPrintingDefaults({ filter: `generalConfigProfileId eq guid'${data.currentGroup.id}'` })
        ).data;

        const model: GeneralConfigProfileModel = {
          ...data.currentGroup,
          name: data.code,
          description: data.description,
          isDefault: false,
          allowMultipleOstypes: data.currentGroup.allowMultipleOstypes ?? false
        };

        res = await postGeneralConfigProfile(model).then(async res => {
          const models: PrintingDefaultModelBulkItemModel[] = [];
          for (let defaultIndex = 0; defaultIndex < printingDefaults.length; defaultIndex++) {
            models.push({
              id: undefined,
              model: {
                ...printingDefaults[defaultIndex],
                generalConfigProfileId: res.data.data?.id,
                labelId: printingDefaults[defaultIndex].labelId ?? ''
              }
            });
          }

          await bulkPutPrintingDefaults(models);
        });

        /**
         * Copy Consignment Settings Group successful
         */
        vigoToast.success('Consignment Settings Group copied', { id: copyToastId });

        return res;
      } catch (e) {
        /**
         * Failed to copy Consignment Settings Group
         */
        vigoToast.error('Error copying Consignment Settings Group', { id: copyToastId });

        throw e;
      }
    }
  });

  const printingDefaultsMutation = useMutation({
    mutationFn: async (data: PrintingDefaultsModels) => {
      if (data.updateModels.length > 0) {
        await bulkPutPrintingDefaults(data.updateModels);
      }

      if (data.deleteModels.length > 0) {
        await bulkDeletePrintingDefaults(data.deleteModels);
      }
    }
  });

  const assignCustomersMutation = useCommonUIFlowMutations();

  const handleSaveGroup = async (res: any, data: { formData: any; id: string }) => {
    const generalConfigProfileModel: GeneralConfigProfileModel = buildConsignmentSettingsGroupModel(data.formData);

    if (id) {
      res = await putGeneralConfigProfile(id, generalConfigProfileModel).then(async () => {
        const models: ConsignmentSettingsGroupModels = generateConsignmentSettingsGroupModels(
          id,
          data.formData.printingDefaults,
          consignmentSettingsGroupDetails?.printingDefaults ?? [],
          consignmentSettingsGroupDetails?.assignedRelationships ?? [],
          assignedCustomersRef?.current ?? []
        );

        await printingDefaultsMutation.mutateAsync({
          deleteModels: models.deleteModels,
          updateModels: models.updateModels
        });

        await assignCustomersMutation.assignCustomersMutation.mutateAsync(models.assignedCustomersModels);
      });
    } else {
      res = await postGeneralConfigProfile(generalConfigProfileModel).then(async res => {
        addRecentHistory?.('Add', generalConfigProfileModel.name);

        const models: ConsignmentSettingsGroupModels = generateConsignmentSettingsGroupModels(
          res.data.data?.id ?? '',
          data.formData.printingDefaults ?? [],
          consignmentSettingsGroupDetails?.printingDefaults ?? [],
          consignmentSettingsGroupDetails?.assignedRelationships ?? [],
          assignedCustomersRef?.current ?? []
        );

        await printingDefaultsMutation.mutateAsync({
          deleteModels: models.deleteModels,
          updateModels: models.updateModels
        });

        await assignCustomersMutation.assignCustomersMutation.mutateAsync(models.assignedCustomersModels);
      });
    }
  };

  const saveMutation = useMutation({
    mutationKey: ['consignmentSettingsGroupSaveMutation'],
    mutationFn: async (data: { formData: any; id: string }) => {
      const consignmentSettingsGroupToastId = vigoToast.loading('Saving Consignment Settings Group');

      let res: any;

      try {
        /**
         * Check if the name is the same as the existing name
         */
        const shouldSave = await handleSameNameCheck(
          uiFlowMode,
          data.formData['name'],
          consignmentSettingsGroupDetails?.consignmentSettingsGroup?.name,
          data.id
        );

        if (shouldSave) {
          if (data.formData['isDefault']) {
            // Do we have an existing default group?
            const defaultGroups = (await getGeneralConfigProfiles({ filter: `isDefault eq true` })).data;

            if (defaultGroups.length > 0 && defaultGroups[0].id !== id) {
              // Ask the user if they want to change the default group

              await confirm(
                `'${defaultGroups[0].name}' is currently the default group. Do you want to change it to '${data.formData['name']}'?`,
                async () => {
                  updateGeneralConfigProfileMutation.mutateAsync({
                    id: defaultGroups[0].id ?? '',
                    model: {
                      ...defaultGroups[0],
                      name: defaultGroups[0].name ?? '',
                      description: defaultGroups[0].description ?? '',
                      isDefault: false,
                      allowMultipleOstypes: defaultGroups[0].allowMultipleOstypes ?? false,
                      allowTotalSpacesEdit: defaultGroups[0].allowTotalSpacesEdit ?? false
                    }
                  });

                  await handleSaveGroup(res, data);

                  /**
                   * Save Consignment Settings Group successful
                   */
                  vigoToast.success('Consignment Settings Group saved', { id: consignmentSettingsGroupToastId });

                  return res;
                },
                () => {
                  vigoToast.dismiss();

                  return;
                }
              );
            } else {
              await handleSaveGroup(res, data);

              /**
               * Save Consignment Settings Group successful
               */
              vigoToast.success('Consignment Settings Group saved', { id: consignmentSettingsGroupToastId });

              return res;
            }
          } else {
            await handleSaveGroup(res, data);

            /**
             * Save Consignment Settings Group successful
             */
            vigoToast.success('Consignment Settings Group saved', { id: consignmentSettingsGroupToastId });

            return res;
          }
        } else {
          /**
           * A group with the same name already exists, reject the promise
           */
          vigoToast.error('A group with the same name already exists', { id: consignmentSettingsGroupToastId });

          return;
        }
      } catch (e) {
        /**
         * Failed to save Consignment Settings Group
         */
        vigoToast.error('Error saving Consignment Settings Group', { id: consignmentSettingsGroupToastId });

        throw e;
      }
    }
  });

  return {
    updateGeneralConfigProfileMutation,
    removeOldDefaultMutation,
    copyConsignmentSettingsGroupMutation,
    saveMutation
  };
};
